import React, { useState, useEffect, useCallback } from 'react';
import PrimaryNavBar from 'components/ui/PrimaryNavBar';
import Icon from 'components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { SkeletonText, SkeletonCard } from '../../components/skeletons';
import ProfileTab from './components/ProfileTab';
import DisplayTab from './components/DisplayTab';
import DashboardTab from './components/DashboardTab';
import NotificationsTab from './components/NotificationsTab';
import PricingTab from './components/PricingTab';

const TABS = [
  { id: 'profile', label: 'Perfil y Cuenta', shortLabel: 'Perfil', icon: 'User', description: 'Información personal y seguridad' },
  { id: 'display', label: 'Pantalla y Locale', shortLabel: 'Pantalla', icon: 'Monitor', description: 'Moneda, formato y apariencia' },
  { id: 'dashboard', label: 'Dashboard', shortLabel: 'Dashboard', icon: 'LayoutDashboard', description: 'Preferencias del panel principal' },
  { id: 'notifications', label: 'Notificaciones', shortLabel: 'Alertas', icon: 'Bell', description: 'Alertas y notificaciones' },
  { id: 'pricing', label: 'Motor de Precios', shortLabel: 'Precios', icon: 'Calculator', description: 'Valores predeterminados para la calculadora' },
];

function Toast({ message, type, onClose }) {
  const config = {
    success: { bg: 'bg-success', icon: 'CheckCircle' },
    error: { bg: 'bg-error', icon: 'XCircle' },
    info: { bg: 'bg-accent', icon: 'Info' },
  };
  const c = config?.[type] || config?.info;
  return (
    <div
      className={`fixed bottom-6 right-6 z-[200] flex items-center gap-3 px-5 py-3.5 rounded-lg shadow-xl text-white animate-slide-down`}
      style={{ minWidth: '280px', maxWidth: '400px', boxShadow: 'var(--shadow-xl)', background: type === 'success' ? 'var(--color-success)' : type === 'error' ? 'var(--color-error)' : 'var(--color-accent)' }}
      role="alert"
    >
      <Icon name={c?.icon} size={18} color="white" strokeWidth={2.5} />
      <span style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '500' }}>{message}</span>
      <button onClick={onClose} className="ml-auto opacity-80 hover:opacity-100 transition-opacity" aria-label="Cerrar">
        <Icon name="X" size={16} color="white" strokeWidth={2.5} />
      </button>
    </div>
  );
}

export default function Preferences() {
  const { user, userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Load preferences on mount
  useEffect(() => {
    if (!user?.id) return;
    const loadPrefs = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('preferences, full_name, company, position')?.eq('id', user?.id)?.single();
        if (!error && data) {
          setPrefs(data?.preferences || {});
        }
      } catch (err) {
        console.error('Failed to load preferences:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPrefs();
  }, [user?.id]);

  const handleSavePrefs = async () => {
    if (!user?.id) return;
    setSaving(true);
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ preferences: prefs, updated_at: new Date()?.toISOString() })?.eq('id', user?.id);
      if (error) throw error;
      showToast('Preferencias guardadas correctamente', 'success');
    } catch (err) {
      showToast(err?.message || 'Error al guardar preferencias', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async (profileData) => {
    if (!user?.id) return;
    const { error } = await supabase?.from('user_profiles')?.update({ ...profileData, updated_at: new Date()?.toISOString() })?.eq('id', user?.id);
    if (error) throw error;
  };

  const activeTabData = TABS?.find((t) => t?.id === activeTab);

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      <PrimaryNavBar />
      <div className="pt-[60px]">
        {/* Page Header */}
        <div className="border-b border-border" style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-sm)' }}>
          <div className="max-w-screen-xl mx-auto px-4 lg:px-8">
            <div className="flex items-center justify-between py-5 gap-4">
              <div className="flex items-center gap-3">
                <div
                  className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
                  style={{ background: 'var(--color-accent)', borderRadius: '10px' }}
                >
                  <Icon name="SlidersHorizontal" size={20} color="white" strokeWidth={2} />
                </div>
                <div>
                  <h1 className="text-foreground" style={{ fontFamily: '"Outfit", sans-serif', fontSize: '1.375rem', fontWeight: '700', lineHeight: 1.2 }}>
                    Preferencias
                  </h1>
                  <p className="text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.8125rem' }}>
                    {activeTabData?.description}
                  </p>
                </div>
              </div>
              {activeTab !== 'profile' && (
                <button
                  onClick={handleSavePrefs}
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-white transition-colors disabled:opacity-60 flex-shrink-0"
                  style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem', fontWeight: '600' }}
                >
                  {saving
                    ? <Icon name="Loader2" size={14} color="white" strokeWidth={2} className="animate-spin" />
                    : <Icon name="Save" size={14} color="white" strokeWidth={2.5} />}
                  {saving ? 'Guardando...' : 'Guardar cambios'}
                </button>
              )}
            </div>

            {/* Tab Navigation */}
            <div className="flex items-center gap-0 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
              {TABS?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                    activeTab === tab?.id
                      ? 'text-accent border-accent' :'text-muted-foreground border-transparent hover:text-foreground hover:border-border'
                  }`}
                  style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.875rem' }}
                >
                  <Icon name={tab?.icon} size={15} color="currentColor" strokeWidth={activeTab === tab?.id ? 2.5 : 2} />
                  <span className="hidden sm:inline">{tab?.label}</span>
                  <span className="sm:hidden">{tab?.shortLabel}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="max-w-screen-xl mx-auto px-4 lg:px-8 py-8">
          {loading ? (
            <div className="max-w-3xl space-y-6">
              {/* Profile tab skeleton */}
              <div className="bg-card border border-border rounded-xl p-6 space-y-4">
                <SkeletonCard height="24px" width="160px" rounded="rounded-md" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Array.from({ length: 4 })?.map((_, i) => (
                    <div key={i} className="space-y-1.5">
                      <SkeletonText width="80px" height="12px" />
                      <SkeletonCard height="38px" rounded="rounded-lg" />
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-card border border-border rounded-xl p-6 space-y-3">
                <SkeletonCard height="24px" width="140px" rounded="rounded-md" />
                {Array.from({ length: 3 })?.map((_, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-border">
                    <SkeletonText width="160px" height="14px" />
                    <SkeletonCard height="24px" width="48px" rounded="rounded-full" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl">
              {activeTab === 'profile' && (
                <ProfileTab
                  user={user}
                  profile={userProfile}
                  onSave={handleSaveProfile}
                  showToast={showToast}
                />
              )}
              {activeTab === 'display' && (
                <DisplayTab prefs={prefs} onChange={setPrefs} />
              )}
              {activeTab === 'dashboard' && (
                <DashboardTab prefs={prefs} onChange={setPrefs} />
              )}
              {activeTab === 'notifications' && (
                <NotificationsTab prefs={prefs} onChange={setPrefs} user={user} />
              )}
              {activeTab === 'pricing' && (
                <PricingTab prefs={prefs} onChange={setPrefs} />
              )}
            </div>
          )}
        </div>
      </div>
      {toast && <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />}
    </div>
  );
}
